import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BuddyAvanzaLegalFooter from "../../components/BuddyAvanzaLegalFooter";
import { ArrowLeft, Mail } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared layout for BUDDY AVANZA legal pages.
 * - Fetches version + last-updated from GET /api/avanzo-b2c/legal-config
 * - Light theme, mobile-first, readable line-length.
 * - Adds legal footer at bottom.
 */
export default function LegalPageLayout({
  documentKey,        // "terms" | "privacy" | "aup" | "ai_notice"
  documentTitle,
  eyebrow,
  intro,
  children,
  testid,
}) {
  const [legal, setLegal] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/avanzo-b2c/legal-config`);
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setLegal(j);
      } catch (_) { /* silent fail: static version fallback below */ }
    })();
    return () => { alive = false; };
  }, []);

  const version = legal?.versions?.[`${documentKey}_version`] || "1.0";
  const lastUpdated = legal?.last_updated_human || "25 de agosto de 2026";
  const contactEmail = legal?.contact_email || "contacto@qhubglobal.com";
  const operator = legal?.operator || "QHUB GLOBAL S.A. de C.V.";

  return (
    <div className="min-h-screen bg-white text-slate-800 flex flex-col" data-testid={testid || `legal-page-${documentKey}`}>
      {/* Top nav */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/85 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/buddy-avanza" data-testid="legal-back-landing"
                 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Volver a BUDDY AVANZA
          </Link>
          <a href={`mailto:${contactEmail}`}
             data-testid="legal-header-contact"
             className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">
            <Mail className="w-4 h-4" /> {contactEmail}
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-4 py-12 md:py-16">
          {eyebrow && (
            <div className="text-[11px] tracking-[3px] uppercase text-[#8b5cf6] font-semibold mb-4">
              {eyebrow}
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 leading-tight"
              data-testid={`${testid || documentKey}-title`}>
            {documentTitle}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500"
               data-testid={`${testid || documentKey}-meta`}>
            <span>Versión <span className="font-semibold text-slate-700" data-testid={`${testid || documentKey}-version`}>{version}</span></span>
            <span className="text-slate-300">·</span>
            <span>Última actualización: {lastUpdated}</span>
            <span className="text-slate-300">·</span>
            <span>{operator}</span>
          </div>

          {intro && (
            <p className="mt-6 text-base text-slate-700 leading-relaxed">
              {intro}
            </p>
          )}

          <div className="mt-8 space-y-8 text-[15px] leading-relaxed text-slate-700 legal-body">
            {typeof children === "function" ? children({ contactEmail, operator }) : children}
          </div>
        </article>
      </main>

      <BuddyAvanzaLegalFooter contactEmail={contactEmail} />

      <style>{`
        .legal-body h2 { font-size: 1.15rem; font-weight: 800; color: #0f172a;
                          margin-top: 1.75rem; margin-bottom: 0.4rem;
                          letter-spacing: -0.01em; }
        .legal-body h3 { font-size: 0.95rem; font-weight: 700; color: #1e293b;
                          margin-top: 1.1rem; margin-bottom: 0.25rem; }
        .legal-body p  { margin: 0.5rem 0; }
        .legal-body ul { list-style: disc; padding-left: 1.25rem; margin: 0.5rem 0; }
        .legal-body li { margin: 0.2rem 0; }
        .legal-body a  { color: #2563eb; text-decoration: underline;
                          text-underline-offset: 2px; }
        .legal-body a:hover { color: #1d4ed8; }
      `}</style>
    </div>
  );
}
